import React, { createContext, useContext, useState, useMemo } from 'react';

const StepContext = createContext();

// Helper: safe localStorage read
const getStored = (key, fallback) => {
    try {
        const val = localStorage.getItem(key);
        return val !== null ? JSON.parse(val) : fallback;
    } catch {
        return fallback;
    }
};

export const StepProvider = ({ children }) => {

    // Map legacy step numbers (7-step system) to new 2-step structure
    const mapLegacyStep = (s) => { if (s >= 3) return 2; return 1; };

    // ✅ Only currentStep is stored locally
    const [currentStep, setCurrentStepState] = useState(() => mapLegacyStep(getStored('step_current', 1)));

    // ❌ DO NOT store completion in localStorage
    const [isCompleted, setIsCompleted] = useState(false);

    const [userExp, setUserExpState] = useState(() => getStored('step_userExp', ''));

    // const totalSteps = 6;

    // ✅ Step 7: Interview round data
    const [interviewData, setInterviewData] = useState({
        is_technical: 0,
        is_one2one: 0,
        is_tech_done: 0,
        is_onetoone_done: 0
    });

    const totalSteps = 2;

    // Persist only currentStep
    const setCurrentStep = (val) => {
        setCurrentStepState(val);
        try {
            localStorage.setItem('step_current', JSON.stringify(val));
        } catch { }
    };

    const setUserExp = (val) => {
        setUserExpState(val);
        try {
            localStorage.setItem('step_userExp', JSON.stringify(val));
        } catch { }
    };

    // Step labels
    const stepLabels = useMemo(() => [
        'Profile Setup',
        'Professional Details',
    ], []);

    // ✅ Step 7: Interview completion logic
    // Complete when: all applicable rounds (is_X === 1) have their done flag (is_X_done === 1)
    // If no rounds assigned (both is_technical === 0 and is_one2one === 0), step 7 is complete by default
    const isInterviewComplete = useMemo(() => {
        const { is_technical, is_one2one, is_tech_done, is_onetoone_done } = interviewData;

        // No rounds assigned → complete by default
        if (is_technical !== 1 && is_one2one !== 1) {
            return true;
        }

        // Check each applicable round
        const techRequired = is_technical === 1;
        const onetooneRequired = is_one2one === 1;
        const techDone = is_tech_done === 1;
        const onetooneDone = is_onetoone_done === 1;

        // All required rounds must be done
        if (techRequired && !techDone) return false;
        if (onetooneRequired && !onetooneDone) return false;

        return true;
    }, [interviewData]);

    // ✅ Progress calculation
    const progressPercent = useMemo(() => {
        if (isCompleted) return 100;
        return Math.round(((currentStep - 1) / totalSteps) * 100);
    }, [currentStep, isCompleted, totalSteps]);

    // Scroll to top on step change
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    // ✅ Reset everything
    const resetProgress = () => {
        setCurrentStepState(1);
        setIsCompleted(false);
        setUserExpState('');
        setInterviewData({
            is_technical: 0,
            is_one2one: 0,
            is_tech_done: 0,
            is_onetoone_done: 0
        });
        try {
            localStorage.removeItem('step_current');
            localStorage.removeItem('step_userExp');
        } catch { }
    };

    return (
        <StepContext.Provider
            value={{
                currentStep,
                setCurrentStep,
                totalSteps,
                progressPercent,
                isCompleted,
                setIsCompleted,   // controlled by backend
                isInterviewComplete,
                interviewData,
                setInterviewData,
                userExp,
                setUserExp,
                stepLabels,
                resetProgress
            }}
        >
            {children}
        </StepContext.Provider>
    );
};

export const useStepContext = () => {
    const context = useContext(StepContext);
    if (!context) {
        console.error('useStepContext used outside <StepProvider>.');
        return {
            currentStep: 1,
            setCurrentStep: () => { },
            totalSteps: 2,
            progressPercent: 0,
            isCompleted: false,
            setIsCompleted: () => { },
            isInterviewComplete: false,
            interviewData: { is_technical: 0, is_one2one: 0, is_tech_done: 0, is_onetoone_done: 0 },
            setInterviewData: () => { },
            userExp: '',
            setUserExp: () => { },
            stepLabels: [],
            resetProgress: () => { }
        };
    }
    return context;
};